import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  message,
  Space,
  Tag,
  Card,
  Descriptions,
  Image,
  Empty,
  Form,
  Select,
  Upload,
  Pagination,
  Alert,
  Typography,
} from 'antd';
import {
  ReloadOutlined,
  EyeOutlined,
  DollarOutlined,
  UploadOutlined,
  DownloadOutlined,
  BankOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../config/apiClient';
import MainLayout from '../layout/layout';
import dayjs from 'dayjs';
import { compressFile } from '../../utils/fileCompression';

const { Text } = Typography;

const AlumnoHistorialPagos = () => {
  const [registros, setRegistros] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalDetalleVisible, setModalDetalleVisible] = useState(false);
  const [modalRegistrarPagoVisible, setModalRegistrarPagoVisible] = useState(false);
  const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
  const [registroParaPago, setRegistroParaPago] = useState(null);
  const [creditos, setCreditos] = useState([]);
  const [loadingCreditos, setLoadingCreditos] = useState(false);
  const [comprobanteFile, setComprobanteFile] = useState(null);
  const [formRegistrarPago] = Form.useForm();
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  const [datosCuenta, setDatosCuenta] = useState(null);

  useEffect(() => {
    loadTiposPago();
    loadRegistros();
    loadDatosCuenta();
  }, []);

  // Cargar datos de cuenta bancaria para transferencias
  const loadDatosCuenta = async () => {
    try {
      const response = await apiClient.get('/configuracion-bot/datos-cuenta');
      if (response.ok) {
        const data = await response.json();
        setDatosCuenta(data);
      }
    } catch (error) {
      console.error('Error al cargar datos de cuenta:', error);
    }
  };

  // Copiar texto al portapapeles
  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success(`${label} copiado al portapapeles`);
    }).catch(() => {
      message.error('Error al copiar');
    });
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cargar tipos de pago
  const loadTiposPago = async () => {
    try {
      const response = await apiClient.get('/mi/tipo-pagos');
      if (response.ok) {
        const data = await response.json();
        setTiposPago(data);
      }
    } catch (error) {
      console.error('Error al cargar tipos de pago:', error);
    }
  };

  // Cargar registros de pago del alumno
  const loadRegistros = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/mi/registro-pagos');
      if (response.ok) {
        const data = await response.json();
        setRegistros(Array.isArray(data) ? data : []);
      } else {
        message.error('Error al cargar los registros de pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar los registros de pago');
    } finally {
      setLoading(false);
    }
  };

  // Cargar créditos de un registro de pago
  const loadCreditos = async (registroPagoId) => {
    setLoadingCreditos(true);
    try {
      const response = await apiClient.get(`/mi-creditos/registro-pago/${registroPagoId}`);
      if (response.ok) {
        const data = await response.json();
        setCreditos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error al cargar créditos:', error);
    } finally {
      setLoadingCreditos(false);
    }
  };

  // Convertir archivo a base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  };

  // Descargar comprobante
  const handleDownloadComprobante = (comprobanteBase64, tipoPago) => {
    try {
      if (!comprobanteBase64) {
        message.error('No hay comprobante disponible');
        return;
      }

      // Determinar tipo MIME y extensión
      let mimeType = 'application/octet-stream';
      let extension = 'bin';
      
      if (comprobanteBase64.startsWith('data:image/')) {
        if (comprobanteBase64.includes('data:image/jpeg') || comprobanteBase64.includes('data:image/jpg')) {
          mimeType = 'image/jpeg';
          extension = 'jpg';
        } else if (comprobanteBase64.includes('data:image/png')) {
          mimeType = 'image/png';
          extension = 'png';
        } else {
          mimeType = 'image/png';
          extension = 'png';
        }
      } else if (comprobanteBase64.startsWith('data:application/pdf')) {
        mimeType = 'application/pdf';
        extension = 'pdf';
      } else {
        // Si no tiene prefijo data:, intentar detectar por el contenido base64
        const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
        try {
          const decoded = atob(base64Data.substring(0, 20));
          if (decoded.startsWith('%PDF') || decoded.startsWith('JVBERi0')) {
            mimeType = 'application/pdf';
            extension = 'pdf';
          } else if (base64Data.startsWith('iVBORw0KG')) {
            mimeType = 'image/png';
            extension = 'png';
          } else if (base64Data.startsWith('/9j/')) {
            mimeType = 'image/jpeg';
            extension = 'jpg';
          }
        } catch (e) {
          // Si no se puede decodificar, usar valores por defecto
          console.warn('No se pudo detectar el tipo de archivo, usando valores por defecto');
        }
      }

      // Convertir base64 a blob
      const base64Data = comprobanteBase64.split(',')[1] || comprobanteBase64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mimeType });

      // Crear URL y descargar
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `comprobante_${tipoPago || 'pago'}_${Date.now()}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar comprobante:', error);
      message.error('Error al descargar el comprobante');
    }
  };

  // Abrir modal de detalle
  const handleVerDetalle = async (record) => {
    setRegistroSeleccionado(record);
    setModalDetalleVisible(true);
    await loadCreditos(record.reg_id);
  };

  // Abrir modal para registrar pago
  const handleRegistrarPago = (record) => {
    setRegistroParaPago(record);
    formRegistrarPago.resetFields();
    setComprobanteFile(null);
    setModalRegistrarPagoVisible(true);
  };

  // Guardar registro de pago
  const handleSaveRegistrarPago = async () => {
    try {
      const values = await formRegistrarPago.validateFields();
      
      let comprobanteBase64 = null;
      let tipoPagoId = null;
      const metodoPago = values.metodoPago;

      // Si es TRANSFERENCIA, validar que haya comprobante
      if (metodoPago === 'TRANSFERENCIA') {
        if (!comprobanteFile) {
          message.error('El comprobante es obligatorio para pagos por transferencia');
          return;
        }
        comprobanteBase64 = await getBase64(comprobanteFile);
      }

      // Obtener el tipoPagoId según el método seleccionado
      if (metodoPago !== 'PENDIENTE') {
        const tipoPago = tiposPago.find(tp => tp.tip_nombre === metodoPago);
        if (tipoPago) {
          tipoPagoId = tipoPago.tip_id;
        }
      }

      const dataToSend = {
        tipoPagoId: tipoPagoId,
        comprobante: comprobanteBase64,
        metodoPago: metodoPago,
      };

      const response = await apiClient.put(`/mi/registro-pagos/${registroParaPago.reg_id}/registrar`, dataToSend);
      if (response.ok) {
        message.success('Pago registrado correctamente');
        setModalRegistrarPagoVisible(false);
        formRegistrarPago.resetFields();
        setComprobanteFile(null);
        setRegistroParaPago(null);
        loadRegistros();
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      if (error.errorFields) {
        return;
      }
      message.error('Error al registrar el pago');
    }
  };

  // Formatear fecha
  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return dayjs(fecha).format('DD/MM/YYYY HH:mm');
  };

  // Obtener color del estado
  const getEstadoColor = (estado) => {
    switch (estado) {
      case 'PAGADO':
        return 'green';
      case 'PENDIENTE':
        return 'orange';
      case 'CANCELADO':
        return 'red';
      default:
        return 'default';
    }
  };

  // Mostrar "Registrar Pago" solo para estados que permiten reintento.
  // Nunca mostrar cuando ya está pendiente o pagado.
  const canShowRegistrarPago = (estado) => {
    return estado === 'CANCELADO' || estado === 'RECHAZADO';
  };

  // Columnas de la tabla
  const columns = [
    {
      title: 'Fecha',
      dataIndex: 'reg_fecha_creacion',
      key: 'reg_fecha_creacion',
      render: (fecha) => formatFecha(fecha),
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Plan',
      dataIndex: 'plan_nombre',
      key: 'plan_nombre',
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Créditos',
      dataIndex: 'pla_cantidad_creditos',
      key: 'pla_cantidad_creditos',
      align: 'center',
      width: isMobile ? 80 : undefined,
    },
    {
      title: 'Total',
      dataIndex: 'reg_total',
      key: 'reg_total',
      render: (total) => `$${parseFloat(total).toFixed(2)}`,
      align: 'right',
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Método de Pago',
      dataIndex: 'tipo_pago_nombre',
      key: 'tipo_pago_nombre',
      render: (tipo) => tipo || '-',
      width: isMobile ? 120 : undefined,
    },
    {
      title: 'Estado',
      dataIndex: 'reg_estado_pago',
      key: 'reg_estado_pago',
      render: (estado) => (
        <Tag color={getEstadoColor(estado)}>
          {estado}
        </Tag>
      ),
      width: isMobile ? 100 : undefined,
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: isMobile ? 150 : 200,
      fixed: isMobile ? 'right' : false,
      render: (_, record) => (
        <Space size="small" wrap>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleVerDetalle(record)}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '' : 'Ver Detalle'}
          </Button>
          {canShowRegistrarPago(record.reg_estado_pago) && (
            <Button
              type="link"
              icon={<DollarOutlined />}
              onClick={() => handleRegistrarPago(record)}
              style={{ color: '#52c41a' }}
              size={isMobile ? 'small' : 'middle'}
            >
              {isMobile ? '' : 'Registrar Pago'}
            </Button>
          )}
        </Space>
      ),
    },
  ];

  return (
    <MainLayout>
      <div style={{ padding: isMobile ? '16px' : '24px' }}>
        <div style={{ marginBottom: isMobile ? 12 : 16 }}>
          <h1 style={{ 
            margin: 0, 
            marginBottom: isMobile ? 12 : 16, 
            color: '#ffffff', 
            fontSize: isMobile ? '20px' : '24px' 
          }}>
            Mi Historial de Pagos
          </h1>
          <Button
            icon={<ReloadOutlined />}
            onClick={loadRegistros}
            loading={loading}
            style={{ marginBottom: isMobile ? 12 : 16 }}
            size={isMobile ? 'small' : 'middle'}
          >
            Actualizar
          </Button>
        </div>

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {registros.length === 0 ? (
              <Empty description="No hay registros de pago" />
            ) : (
              registros.map(registro => (
                <Card
                  key={registro.reg_id}
                  hoverable
                  style={{
                    borderLeft: '4px solid #1890ff',
                    borderRadius: '8px',
                    background: '#2d2d2d',
                    borderColor: '#404040',
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 'bold', fontSize: isMobile ? '15px' : '16px', marginBottom: '8px', color: '#ffffff' }}>
                          {registro.plan_nombre}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Créditos: {registro.pla_cantidad_creditos}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Total: ${parseFloat(registro.reg_total).toFixed(2)}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Método: {registro.tipo_pago_nombre || '-'}
                        </div>
                        <div style={{ fontSize: isMobile ? '12px' : '13px', color: '#b0b0b0', marginBottom: '4px' }}>
                          Fecha: {formatFecha(registro.reg_fecha_creacion)}
                        </div>
                      </div>
                      <Tag color={getEstadoColor(registro.reg_estado_pago)} style={{ flexShrink: 0 }}>
                        {registro.reg_estado_pago}
                      </Tag>
                    </div>
                    <Space wrap style={{ width: '100%' }}>
                      <Button
                        type="link"
                        icon={<EyeOutlined />}
                        onClick={() => handleVerDetalle(registro)}
                        style={{ color: '#1890ff', padding: isMobile ? '4px 8px' : undefined }}
                        size={isMobile ? 'small' : 'middle'}
                      >
                        Ver Detalle
                      </Button>
                      {canShowRegistrarPago(registro.reg_estado_pago) && (
                        <Button
                          type="link"
                          icon={<DollarOutlined />}
                          onClick={() => handleRegistrarPago(registro)}
                          style={{ color: '#52c41a', padding: isMobile ? '4px 8px' : undefined }}
                          size={isMobile ? 'small' : 'middle'}
                        >
                          Registrar Pago
                        </Button>
                      )}
                    </Space>
                  </div>
                </Card>
              ))
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={registros}
            loading={loading}
            rowKey="reg_id"
            pagination={false}
            scroll={{ x: isMobile ? 1000 : 'max-content' }}
            size={isMobile ? 'small' : 'middle'}
          />
        )}

        {/* Modal de detalle */}
        <Modal
          title="Detalle del Registro de Pago"
          open={modalDetalleVisible}
          onCancel={() => {
            setModalDetalleVisible(false);
            setRegistroSeleccionado(null);
            setCreditos([]);
          }}
          footer={[
            <Button key="close" onClick={() => {
              setModalDetalleVisible(false);
              setRegistroSeleccionado(null);
              setCreditos([]);
            }}>
              Cerrar
            </Button>
          ]}
          width={isMobile ? '95%' : 800}
        >
          {registroSeleccionado && (
            <div>
              <Descriptions
                title="Información del Pago"
                bordered
                column={isMobile ? 1 : 2}
                style={{ marginBottom: 24 }}
                size={isMobile ? 'small' : 'default'}
              >
                <Descriptions.Item label="Plan">
                  {registroSeleccionado.plan_nombre}
                </Descriptions.Item>
                <Descriptions.Item label="Total">
                  ${parseFloat(registroSeleccionado.reg_total).toFixed(2)}
                </Descriptions.Item>
                <Descriptions.Item label="Método de Pago">
                  {registroSeleccionado.tipo_pago_nombre || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="Estado">
                  <Tag color={getEstadoColor(registroSeleccionado.reg_estado_pago)}>
                    {registroSeleccionado.reg_estado_pago}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Fecha de Creación">
                  {formatFecha(registroSeleccionado.reg_fecha_creacion)}
                </Descriptions.Item>
                {registroSeleccionado.reg_fecha_pago && (
                  <Descriptions.Item label="Fecha de Pago">
                    {formatFecha(registroSeleccionado.reg_fecha_pago)}
                  </Descriptions.Item>
                )}
                {registroSeleccionado.reg_comprobante && (
                  <Descriptions.Item label="Comprobante">
                    <div>
                      {registroSeleccionado.reg_comprobante.startsWith('data:image/') ? (
                        <Image
                          width={isMobile ? 150 : 200}
                          src={registroSeleccionado.reg_comprobante}
                          alt="Comprobante"
                          style={{ maxWidth: '100%', marginBottom: 8 }}
                        />
                      ) : (
                        <div style={{ 
                          padding: isMobile ? 12 : 16, 
                          backgroundColor: '#2d2d2d', 
                          borderRadius: 4,
                          marginBottom: 8,
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: isMobile ? 36 : 48, marginBottom: 8 }}>📄</div>
                          <div style={{ color: '#ffffff', fontSize: isMobile ? '13px' : '14px' }}>Archivo PDF</div>
                        </div>
                      )}
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => handleDownloadComprobante(
                          registroSeleccionado.reg_comprobante,
                          registroSeleccionado.tipo_pago_nombre
                        )}
                        style={{ marginTop: 8 }}
                        size={isMobile ? 'small' : 'middle'}
                        block={isMobile}
                      >
                        Descargar Comprobante
                      </Button>
                    </div>
                  </Descriptions.Item>
                )}
              </Descriptions>

              <div style={{ marginTop: 24 }}>
                <h3 style={{ color: '#ffffff', marginBottom: 16 }}>Créditos Asociados</h3>
                {loadingCreditos ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    Cargando créditos...
                  </div>
                ) : creditos.length === 0 ? (
                  <Empty description="No hay créditos asociados" />
                ) : (
                  <Table
                    dataSource={creditos}
                    rowKey="cre_id"
                    pagination={false}
                    size="small"
                    scroll={{ x: isMobile ? 600 : 'max-content' }}
                    columns={[
                      {
                        title: 'Fecha de Adquisición',
                        dataIndex: 'cre_fecha_adquisicion',
                        key: 'cre_fecha_adquisicion',
                        render: (fecha) => formatFecha(fecha),
                        width: isMobile ? 140 : undefined,
                      },
                      {
                        title: 'Fecha de Vencimiento',
                        dataIndex: 'cre_fecha_vencimiento',
                        key: 'cre_fecha_vencimiento',
                        render: (fecha) => {
                          const fechaVenc = dayjs(fecha);
                          const hoy = dayjs();
                          const vencido = fechaVenc.isBefore(hoy);
                          return (
                            <span style={{ color: vencido ? '#ff4d4f' : '#52c41a' }}>
                              {formatFecha(fecha)}
                              {vencido && ' (Vencido)'}
                            </span>
                          );
                        },
                        width: isMobile ? 160 : undefined,
                      },
                      {
                        title: 'Estado',
                        dataIndex: 'cre_utilizado',
                        key: 'cre_utilizado',
                        render: (utilizado) => (
                          <Tag color={utilizado ? 'blue' : 'green'}>
                            {utilizado ? 'Utilizado' : 'Disponible'}
                          </Tag>
                        ),
                        width: isMobile ? 100 : undefined,
                      },
                    ]}
                  />
                )}
              </div>
            </div>
          )}
        </Modal>

        {/* Modal para registrar pago */}
        <Modal
          title={`Registrar Pago`}
          open={modalRegistrarPagoVisible}
          onOk={handleSaveRegistrarPago}
          onCancel={() => {
            setModalRegistrarPagoVisible(false);
            formRegistrarPago.resetFields();
            setComprobanteFile(null);
            setRegistroParaPago(null);
          }}
          okText="Registrar Pago"
          cancelText="Cancelar"
          width={isMobile ? '95%' : 600}
        >
          {registroParaPago && (
            <div style={{ marginBottom: 16, padding: 12, backgroundColor: '#f0f2f5', borderRadius: 4, color: '#000000' }}>
              <div><strong>Plan:</strong> {registroParaPago.plan_nombre}</div>
              <div><strong>Total:</strong> ${parseFloat(registroParaPago.reg_total).toFixed(2)}</div>
            </div>
          )}

          <Form
            form={formRegistrarPago}
            layout="vertical"
          >
            <Form.Item
              name="metodoPago"
              label="Método de Pago"
              rules={[
                { required: true, message: 'Debe seleccionar un método de pago' },
              ]}
            >
              <Select
                placeholder="Seleccione el método de pago"
                onChange={(value) => {
                  if (value !== 'TRANSFERENCIA') {
                    formRegistrarPago.setFieldsValue({ comprobante: null });
                    setComprobanteFile(null);
                  }
                }}
              >
                {tiposPago.map(tipo => (
                  <Select.Option key={tipo.tip_id} value={tipo.tip_nombre}>
                    {tipo.tip_nombre}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              noStyle
              shouldUpdate={(prevValues, currentValues) => 
                prevValues.metodoPago !== currentValues.metodoPago
              }
            >
              {({ getFieldValue }) => {
                const metodoPago = getFieldValue('metodoPago');
                const mostrarComprobante = metodoPago === 'TRANSFERENCIA';
                
                return mostrarComprobante ? (
                  <>
                    {/* Datos de cuenta bancaria */}
                    {datosCuenta && (datosCuenta.titularCuenta || datosCuenta.cbu || datosCuenta.alias) && (
                      <Alert
                        type="info"
                        icon={<BankOutlined />}
                        message={
                          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>
                            Datos para Transferencia
                          </span>
                        }
                        description={
                          <div style={{ marginTop: '8px' }}>
                            {datosCuenta.titularCuenta && (
                              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <Text type="secondary">Titular: </Text>
                                  <Text strong>{datosCuenta.titularCuenta}</Text>
                                </div>
                                <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(datosCuenta.titularCuenta, 'Titular')}
                                />
                              </div>
                            )}
                            {datosCuenta.nombreBanco && (
                              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <Text type="secondary">Banco: </Text>
                                  <Text strong>{datosCuenta.nombreBanco}</Text>
                                </div>
                                <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(datosCuenta.nombreBanco, 'Banco')}
                                />
                              </div>
                            )}
                            {datosCuenta.cbu && (
                              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <Text type="secondary">CBU: </Text>
                                  <Text strong>{datosCuenta.cbu}</Text>
                                </div>
                                <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(datosCuenta.cbu, 'CBU')}
                                />
                              </div>
                            )}
                            {datosCuenta.alias && (
                              <div style={{ marginBottom: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <Text type="secondary">Alias: </Text>
                                  <Text strong>{datosCuenta.alias}</Text>
                                </div>
                                <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(datosCuenta.alias, 'Alias')}
                                />
                              </div>
                            )}
                            {datosCuenta.numeroCuenta && (
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div>
                                  <Text type="secondary">Nro. Cuenta: </Text>
                                  <Text strong>{datosCuenta.numeroCuenta}</Text>
                                </div>
                                <Button 
                                  type="text" 
                                  size="small" 
                                  icon={<CopyOutlined />}
                                  onClick={() => copyToClipboard(datosCuenta.numeroCuenta, 'Número de cuenta')}
                                />
                              </div>
                            )}
                          </div>
                        }
                        style={{ marginBottom: '16px' }}
                      />
                    )}
                    <Form.Item
                      name="comprobante"
                      label="Comprobante de Transferencia"
                      rules={[
                        { required: true, message: 'Debe cargar el comprobante de transferencia' },
                      ]}
                    >
                      <Upload
                        accept="image/*,application/pdf"
                        beforeUpload={async (file) => {
                          try {
                            // Comprimir el archivo
                            const compressedFile = await compressFile(file);
                            setComprobanteFile(compressedFile);
                            // Actualizar el campo del formulario para que la validación funcione
                            formRegistrarPago.setFieldsValue({ comprobante: compressedFile.name });
                            return false; // Prevent upload
                          } catch (error) {
                            message.error(error.message || 'Error al procesar el archivo');
                            return false;
                          }
                        }}
                        onRemove={() => {
                          setComprobanteFile(null);
                          formRegistrarPago.setFieldsValue({ comprobante: null });
                        }}
                        maxCount={1}
                      >
                        <Button icon={<UploadOutlined />}>Seleccionar Archivo (Imagen o PDF)</Button>
                      </Upload>
                      <div style={{ fontSize: '12px', color: '#b0b0b0', marginTop: 4 }}>
                        Formatos aceptados: JPG, PNG, PDF (máx. 5MB)
                      </div>
                    </Form.Item>
                  </>
                ) : null;
              }}
            </Form.Item>
          </Form>
        </Modal>
      </div>
    </MainLayout>
  );
};

export default AlumnoHistorialPagos;

